/**
 * Dashboard de gestion de l'abonnement et de la facturation
 * Architecture: Component → Hook (TanStack Query) → Service (subscriptionsAPI)
 *
 * Consomme le backend getQuotasAndUsage() qui retourne :
 * { plan, planName, priceMonthly, features, status, currentPeriodEnd, trialEndsAt, cancelAtPeriodEnd, quotas: [{metric, limit, used, remaining, percentage}] }
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useSubscriptionQuotas,
  useSyncSubscription,
  useOpenBillingPortal,
} from '@/hooks/useSubscriptions';
import type { QuotaItem } from '@/hooks/useSubscriptions';
import { toFrench } from '@/utils/dateUtils';
import {
  CreditCard,
  Users,
  FileText,
  Receipt,
  HardDrive,
  Check,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui-v2/Button';
import { notifications } from '@/lib/notifications';

/** Trouver un quota par metric dans le tableau */
function findQuota(quotas: QuotaItem[], metric: string): QuotaItem | undefined {
  return quotas.find((q) => q.metric === metric);
}

export default function BillingDashboard() {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // TanStack Query — données serveur
  const { data: quotasData, isLoading: loading, error: queryError, refetch } = useSubscriptionQuotas();

  // Mutations
  const syncMutation = useSyncSubscription();
  const portalMutation = useOpenBillingPortal();

  const handleManageSubscription = async () => {
    setActionLoading(true);
    try {
      await portalMutation.mutateAsync();
    } catch (err: any) {
      notifications.error(`Erreur: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncSubscription = async () => {
    setActionLoading(true);
    try {
      await syncMutation.mutateAsync();
      notifications.success(t('billing.dashboard.syncSuccess', 'Statut synchronisé avec Stripe'));
      // refetch quotas after sync — invalidateQueries already called by useSyncSubscription
    } catch (err: any) {
      notifications.error(`Erreur: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getPlanColor = (plan: string) => {
    const colors: Record<string, string> = {
      free: 'bg-gray-100 text-gray-800',
      starter: 'bg-teal-100 text-teal-800',
      pro: 'bg-teal-100 text-teal-800',
      business: 'bg-green-100 text-green-800',
      enterprise: 'bg-orange-100 text-orange-800',
    };
    return colors[plan.toLowerCase()] || colors.free;
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Le plan est payant dès qu'il n'est pas FREE, même si le statut est INCOMPLETE
  const isPaid = useMemo(() => {
    if (!quotasData) return false;
    return quotasData.plan.toUpperCase() !== 'FREE' && quotasData.priceMonthly > 0;
  }, [quotasData]);

  // Extract individual quotas from the array (metric names match seed: camelCase)
  const usersQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxUsers') : undefined, [quotasData]);
  const clientsQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxClients') : undefined, [quotasData]);
  const productsQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxProducts') : undefined, [quotasData]);
  const invoicesQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxInvoicesPerMonth') : undefined, [quotasData]);
  const storageQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxStorageMb') : undefined, [quotasData]);
  const templatesQuota = useMemo(() => quotasData ? findQuota(quotasData.quotas, 'maxTemplates') : undefined, [quotasData]);

  // Feature label map — covers both camelCase and snake_case keys from DB
  const FEATURE_LABELS: Record<string, string> = useMemo(() => ({
    facturx: t('billing.dashboard.facturX', 'Factur-X EN 16931'),
    treasury: t('billing.dashboard.treasury', 'Trésorerie automatique'),
    signatures: t('billing.dashboard.signatures', 'Signature électronique'),
    signature: t('billing.dashboard.signatures', 'Signature électronique'),
    ocr: t('billing.dashboard.ocr', 'OCR - Reconnaissance de documents'),
    bankReconciliation: t('billing.dashboard.bankReconciliation', 'Rapprochement bancaire'),
    bank_reconciliation: t('billing.dashboard.bankReconciliation', 'Rapprochement bancaire'),
    archiving: t('billing.dashboard.archiving', 'Archivage probant 10 ans'),
    archival: t('billing.dashboard.archiving', 'Archivage probant 10 ans'),
    accounting_export: t('billing.dashboard.accountingExport', 'Export comptable'),
    accountingExport: t('billing.dashboard.accountingExport', 'Export comptable'),
    api: t('billing.dashboard.apiAccess', 'API publique'),
    webhooks: t('billing.dashboard.webhooks', 'Webhooks personnalisés'),
    multiCompany: t('billing.dashboard.multiCompanies', 'Multi-sociétés'),
    multi_companies: t('billing.dashboard.multiCompanies', 'Multi-sociétés'),
    sla: t('billing.dashboard.sla', 'SLA 99.9% garanti'),
    dedicatedSupport: t('billing.dashboard.dedicatedSupport', 'Support dédié'),
    priority_support: t('billing.dashboard.dedicatedSupport', 'Support prioritaire'),
    aiInsights: t('billing.dashboard.aiInsights', 'Insights IA'),
    sso: t('billing.dashboard.sso', 'SSO (Single Sign-On)'),
  }), [t]);

  // Extract displayable features (boolean=true, with a known label)
  const featureEntries: [string, string][] = useMemo(() => {
    if (!quotasData?.features) return [];
    return Object.entries(quotasData.features)
      .filter(([key, val]) => val === true && FEATURE_LABELS[key])
      .map(([key]) => [key, FEATURE_LABELS[key]]);
  }, [quotasData, FEATURE_LABELS]);

  // Check if any quota is near limit
  const hasWarning = useMemo(() => {
    if (!quotasData) return false;
    return quotasData.quotas.some((q) => q.percentage >= 80);
  }, [quotasData]);

  const error = queryError instanceof Error ? queryError.message : queryError ? String(queryError) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 text-center">
          <p className="text-red-600">{error}</p>
          <Button variant="primary" onClick={() => refetch()} className="mt-4">
            {t('common.actions.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!quotasData) return null;

  const isFree = quotasData.plan.toUpperCase() === 'FREE' || quotasData.status.toUpperCase() === 'NONE';
  const statusUp = quotasData.status.toUpperCase();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900">
            {t('billing.dashboard.title')}
          </h1>
          <p className="mt-2 text-base text-gray-500 font-light tracking-wide">
            {t('billing.dashboard.subtitle')}
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate('/pricing')}>
          {t('billing.dashboard.changePlan')}
        </Button>
      </div>

      {/* Current Plan Card */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CreditCard className="h-8 w-8 text-teal-600 mr-4" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('billing.dashboard.currentPlan')}
              </h2>
              <div className="mt-2">
                <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${getPlanColor(quotasData.plan)}`}>
                  {quotasData.planName || quotasData.plan.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            {isPaid ? (
              <>
                <div className="text-3xl font-bold text-gray-900">
                  {quotasData.priceMonthly.toFixed(2)} &euro;
                </div>
                <p className="text-sm text-gray-600">{t('common.perMonth')}</p>
              </>
            ) : (
              <div className="text-3xl font-bold text-gray-900">{t('common.free')}</div>
            )}
          </div>
        </div>

        {/* Subscription Status */}
        {!isFree && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('billing.dashboard.status')}</p>
                <p className="mt-1 font-semibold text-gray-900">
                  {statusUp === 'ACTIVE' && <span className="text-green-600">{t('billing.dashboard.statusActive')}</span>}
                  {statusUp === 'TRIALING' && <span className="text-teal-600">{t('billing.dashboard.statusTrialing')}</span>}
                  {statusUp === 'CANCELLED' && <span className="text-red-600">{t('billing.dashboard.statusCanceled')}</span>}
                  {statusUp === 'PAST_DUE' && <span className="text-orange-600">{t('billing.dashboard.statusPastDue')}</span>}
                  {statusUp === 'INCOMPLETE' && <span className="text-yellow-600">{t('billing.dashboard.statusIncomplete', 'En attente de paiement')}</span>}
                </p>
              </div>
              {quotasData.currentPeriodEnd && (
                <div>
                  <p className="text-sm text-gray-600">{t('billing.dashboard.nextInvoice')}</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {toFrench(quotasData.currentPeriodEnd)}
                  </p>
                </div>
              )}
              {quotasData.trialEndsAt && new Date(quotasData.trialEndsAt) > new Date() && (
                <div>
                  <p className="text-sm text-gray-600">{t('billing.dashboard.trialEnds')}</p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {toFrench(quotasData.trialEndsAt)}
                  </p>
                </div>
              )}
            </div>

            {/* Subscription Actions */}
            <div className="mt-6 flex flex-wrap gap-3">
              {isPaid && !isFree && (
                <Button
                  variant="primary"
                  onClick={handleManageSubscription}
                  disabled={actionLoading}
                  size="sm"
                  icon={<ExternalLink className="h-4 w-4" />}
                >
                  {t('billing.dashboard.manageSubscription', 'Gérer mon abonnement')}
                </Button>
              )}
              {quotasData.cancelAtPeriodEnd && (
                <div className="w-full bg-orange-50/20 backdrop-blur-sm border border-orange-200/30 rounded-2xl p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-orange-800">
                        {t('billing.dashboard.subscriptionEndsOn')}{' '}
                        <strong>
                          {quotasData.currentPeriodEnd && toFrench(quotasData.currentPeriodEnd)}
                        </strong>
                      </p>
                      <Button
                        variant="primary"
                        onClick={handleManageSubscription}
                        disabled={actionLoading}
                        size="sm"
                        className="mt-2"
                      >
                        {t('billing.dashboard.manageSubscription', 'Gérer mon abonnement')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {statusUp === 'INCOMPLETE' && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="primary"
                    onClick={handleSyncSubscription}
                    disabled={actionLoading}
                    size="sm"
                    icon={<RefreshCw className="h-4 w-4" />}
                  >
                    {t('billing.dashboard.syncStatus', 'Vérifier le paiement')}
                  </Button>
                  <span className="text-sm text-gray-500">
                    {t('billing.dashboard.syncHint', 'Si vous avez déjà payé, cliquez pour mettre à jour le statut')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Usage Statistics — only show quotas that exist */}
      {quotasData.quotas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Utilisateurs */}
          {usersQuota && usersQuota.limit > 0 && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-8 w-8 text-teal-600" />
                <span className="text-sm font-semibold text-gray-600">
                  {usersQuota.limit === -1
                    ? t('common.unlimited')
                    : `${usersQuota.used} / ${usersQuota.limit}`}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('billing.dashboard.usersLabel', 'Utilisateurs')}
              </h3>
              {usersQuota.limit > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(usersQuota.percentage)}`}
                    style={{ width: `${Math.min(usersQuota.percentage, 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Clients */}
          {clientsQuota && clientsQuota.limit > 0 && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <FileText className="h-8 w-8 text-teal-600" />
                <span className="text-sm font-semibold text-gray-600">
                  {clientsQuota.limit === -1
                    ? t('common.unlimited')
                    : `${clientsQuota.used} / ${clientsQuota.limit}`}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('billing.dashboard.clientsLabel', 'Clients')}
              </h3>
              {clientsQuota.limit > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(clientsQuota.percentage)}`}
                    style={{ width: `${Math.min(clientsQuota.percentage, 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Factures */}
          {invoicesQuota && invoicesQuota.limit > 0 && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <Receipt className="h-8 w-8 text-green-600" />
                <span className="text-sm font-semibold text-gray-600">
                  {invoicesQuota.limit === -1
                    ? t('common.unlimited')
                    : `${invoicesQuota.used} / ${invoicesQuota.limit}`}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('billing.dashboard.invoicesThisMonth')}
              </h3>
              {invoicesQuota.limit > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(invoicesQuota.percentage)}`}
                    style={{ width: `${Math.min(invoicesQuota.percentage, 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Stockage */}
          {storageQuota && (
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <HardDrive className="h-8 w-8 text-teal-600" />
                <span className="text-sm font-semibold text-gray-600">
                  {storageQuota.limit === -1
                    ? t('common.unlimited')
                    : `${storageQuota.used} MB / ${storageQuota.limit} MB`}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('billing.dashboard.storageUsed')}
              </h3>
              {storageQuota.limit > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(storageQuota.percentage)}`}
                    style={{ width: `${Math.min(storageQuota.percentage, 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quota Warnings */}
      {hasWarning && (
        <div className="bg-yellow-50/20 backdrop-blur-sm border border-yellow-200/30 rounded-2xl p-6 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                {t('billing.dashboard.limitWarningTitle')}
              </h3>
              <p className="text-yellow-800 mb-4">
                {t('billing.dashboard.limitWarningMessage')}
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/pricing')}
                icon={<TrendingUp className="h-5 w-5" />}
              >
                {t('billing.dashboard.viewSuperiorPlans')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Features */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl border border-white/30 p-6 hover:shadow-2xl transition-all duration-300">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {t('billing.dashboard.includedFeatures')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Quotas as features */}
          {usersQuota && (
            <div className="flex items-center">
              <Users className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {usersQuota.limit === -1
                  ? t('billing.dashboard.unlimitedUsers')
                  : t('billing.dashboard.usersUpTo', { count: usersQuota.limit })}
              </span>
            </div>
          )}
          {clientsQuota && (
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {clientsQuota.limit === -1
                  ? t('billing.dashboard.unlimitedClients', 'Clients illimités')
                  : t('billing.dashboard.clientsUpTo', { count: clientsQuota.limit, defaultValue: "Jusqu'à {{count}} client(s)" })}
              </span>
            </div>
          )}
          {productsQuota && (
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {productsQuota.limit === -1
                  ? t('billing.dashboard.unlimitedProducts', 'Produits illimités')
                  : t('billing.dashboard.productsUpTo', { count: productsQuota.limit, defaultValue: "Jusqu'à {{count}} produit(s)" })}
              </span>
            </div>
          )}
          {invoicesQuota && (
            <div className="flex items-center">
              <Receipt className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {invoicesQuota.limit === -1
                  ? t('billing.dashboard.unlimitedInvoices')
                  : t('billing.dashboard.invoicesPerMonth', { count: invoicesQuota.limit })}
              </span>
            </div>
          )}
          {storageQuota && (
            <div className="flex items-center">
              <HardDrive className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {storageQuota.limit === -1
                  ? t('billing.dashboard.unlimitedStorage')
                  : t('billing.dashboard.storageMB', { count: storageQuota.limit })}
              </span>
            </div>
          )}
          {templatesQuota && (
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-gray-400 mr-3" />
              <span className="text-gray-700">
                {templatesQuota.limit === -1
                  ? t('billing.dashboard.unlimitedTemplates', 'Templates illimités')
                  : t('billing.dashboard.templatesUpTo', { count: templatesQuota.limit, defaultValue: "Jusqu'à {{count}} template(s)" })}
              </span>
            </div>
          )}

          {/* Plan features from JSON — rendered dynamically to handle both naming conventions */}
          {featureEntries.map(([key, label]) => (
            <div key={key} className="flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-3" />
              <span className="text-gray-700">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
